import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Eye, Loader2, X } from "lucide-react";
import { API } from "@/App";
import { renderQuotePdfBytes } from "@/components/quotes/QuotePdfPreview";

export const quotePdfFileName = (q) =>
  `Quote-${(q?.quote_number || q?.id || "summary").replace(/[^A-Za-z0-9._-]/g, "_")}.pdf`;

// Fetch the saved quote and render the exact Preview/Download PDF bytes.
export async function buildQuotePdf(quoteId, token, user) {
  const fresh = await axios.get(`${API}/quotes/${quoteId}`, { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 });
  return renderQuotePdfBytes(fresh.data, user);
}

// "Preview PDF" link + in-Compose viewer showing the exact attachment.
export const QuoteAttachmentPreview = ({ quote, token, user }) => {
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState(null);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const open = async () => {
    setLoading(true);
    try {
      const bytes = await buildQuotePdf(quote.id, token, user);
      setUrl(URL.createObjectURL(new Blob([bytes], { type: "application/pdf" })));
    } catch (err) {
      console.error("[compose] quote PDF preview failed", err);
      toast.error("Couldn't prepare the quote PDF — try again");
    }
    setLoading(false);
  };

  return (
    <>
      <button type="button" onClick={open} disabled={loading} data-testid="quote-attachment-preview-btn"
        style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 4, background: "none", border: "none", padding: 0, fontSize: 11, fontWeight: 600, color: "var(--blue)", cursor: loading ? "default" : "pointer", textDecoration: "underline" }}>
        {loading ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <Eye style={{ width: 11, height: 11 }} />}
        {loading ? "Preparing…" : "Preview PDF"}
      </button>
      {url && (
        <div onClick={(e) => { e.stopPropagation(); setUrl(null); }} data-testid="quote-attachment-preview-modal"
          style={{ position: "fixed", inset: 0, zIndex: 1200, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: "min(900px, 94vw)", height: "90vh", background: "var(--surface, #fff)", borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 50px rgba(0,0,0,0.3)" }}>
            <div style={{ display: "flex", alignItems: "center", padding: "10px 14px", borderBottom: "0.5px solid var(--border)" }}>
              <span data-testid="quote-attachment-preview-name" style={{ fontSize: 12, fontWeight: 600, color: "var(--text-primary)" }}>{quotePdfFileName(quote)}</span>
              <span style={{ fontSize: 11, color: "var(--text-tertiary)", marginLeft: 8 }}>A fresh copy is built again when you send</span>
              <a href={url} target="_blank" rel="noreferrer" data-testid="quote-attachment-preview-newtab"
                style={{ marginLeft: "auto", marginRight: 12, fontSize: 11, color: "var(--blue)", textDecoration: "underline" }}>Open in new tab</a>
              <button type="button" onClick={() => setUrl(null)} data-testid="quote-attachment-preview-close" aria-label="Close preview"
                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", display: "flex" }}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>
            <iframe title="Quote PDF preview" src={url} data-testid="quote-attachment-preview-frame" style={{ flex: 1, border: "none", width: "100%" }} />
          </div>
        </div>
      )}
    </>
  );
};
